import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
import { CustomModal } from "../../../../components/modal";
import {
  ConditionType,
  OrchestrationCondition,
  getConditionType,
} from "../domain/orchestrationCriteria.entity";
import { FormattedMessage, useIntl } from "react-intl";
import {
  orchestratorMessages,
  criteriaModalMessages as messages,
} from "../../orchestrator.messages";
import { OccupationOption } from "../hooks";
import { useCriteriaForm } from "../hooks/useCriteriaForm";
import { OccupancyInput } from "./criteria/OccupancyInput";
import { StringListInput } from "./criteria/StringListInput";
import { DayOfWeekInput } from "./criteria/DayOfWeekInput";
import { WindowsInput } from "./criteria/WindowsInput";
import { RangeInput } from "./criteria/RangeInput";
import { DeviceInput } from "./criteria/DeviceInput";
import { SeasonInput } from "./criteria/SeasonInput";

interface AddCriteriaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (condition: OrchestrationCondition) => void;
  editingCondition?: OrchestrationCondition;
  editingIndex?: number;
  existingConditionTypes: ConditionType[];
  occupations: OccupationOption[];
}

export function AddCriteriaModal({
  isOpen,
  onClose,
  onSave,
  editingCondition,
  existingConditionTypes,
  occupations,
}: AddCriteriaModalProps) {
  const intl = useIntl();
  const form = useCriteriaForm({ isOpen, editingCondition, onSave });

  const availableConditionTypes = useMemo(() => {
    const editingType = editingCondition ? getConditionType(editingCondition) : null;
    return Object.values(ConditionType).filter(
      type => !existingConditionTypes.includes(type) || type === editingType
    );
  }, [existingConditionTypes, editingCondition]);

  const renderValueInputs = () => {
    switch (form.selectedType) {
      case ConditionType.OCCUPANCY:
        return (
          <OccupancyInput
            occupations={occupations}
            selectedOccupations={form.selectedOccupations}
            setSelectedOccupations={form.setSelectedOccupations}
            occupancyTypeFilters={form.occupancyTypeFilters}
            setOccupancyTypeFilters={form.setOccupancyTypeFilters}
          />
        );
      case ConditionType.REFERRER:
      case ConditionType.UTM_SOURCE:
      case ConditionType.ORIGIN:
        return <StringListInput value={form.stringListValue} onChange={form.setStringListValue} />;
      case ConditionType.CHECK_IN_DAY:
      case ConditionType.CHECK_OUT_DAY:
        return (
          <DayOfWeekInput
            windows={form.windows}
            setWindows={form.setWindows}
            onDayToggle={form.handleDayToggle}
          />
        );
      case ConditionType.BOOKING_WINDOW:
      case ConditionType.TRAVEL_WINDOW:
        return (
          <WindowsInput
            type={form.selectedType}
            windows={form.windows}
            setWindows={form.setWindows}
            onDayToggle={form.handleDayToggle}
          />
        );
      case ConditionType.LENGTH_OF_STAY:
      case ConditionType.NUMBER_OF_ROOMS:
        return (
          <RangeInput
            minValue={form.minValue}
            maxValue={form.maxValue}
            onMinChange={form.setMinValue}
            onMaxChange={form.setMaxValue}
          />
        );
      case ConditionType.DEVICE:
        return <DeviceInput deviceTypes={form.deviceTypes} onToggle={form.handleDeviceToggle} />;
      case ConditionType.SEASON:
        return (
          <SeasonInput
            seasons={form.seasons}
            hemisphere={form.hemisphere}
            onToggle={form.handleSeasonToggle}
            onHemisphereChange={form.setHemisphere}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        editingCondition
          ? intl.formatMessage(messages.editConditionTitle)
          : intl.formatMessage(messages.addConditionTitle)
      }
      size="lg">
      <div className="space-y-4 p-4">
        <div>
          <label className="block font-medium text-gray-700 mb-1">
            <FormattedMessage {...messages.conditionType} />
          </label>
          <select
            value={form.selectedType}
            onChange={e => form.handleTypeChange(e.target.value as ConditionType)}
            disabled={!!editingCondition}
            className="w-full border rounded px-3 py-2 disabled:bg-gray-100">
            <option value="">
              <FormattedMessage {...messages.selectCondition} />
            </option>
            {availableConditionTypes.map(type => (
              <option key={type} value={type}>
                <FormattedMessage {...orchestratorMessages[type]} />
              </option>
            ))}
          </select>
        </div>

        <div>{renderValueInputs()}</div>

        <div className="flex justify-start gap-2 pt-4">
          <Button
            className="px-4 py-2"
            variant="primary"
            type="button"
            disabled={!form.selectedType}
            onClick={form.handleSave}>
            {editingCondition
              ? intl.formatMessage(messages.update)
              : intl.formatMessage(messages.add)}
          </Button>
          <Button className="px-4 py-2" variant="light" type="button" onClick={onClose}>
            <FormattedMessage {...messages.cancel} />
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
